import React from 'react';
import styled from 'styled-components';
import { useMutation } from '@apollo/client';
import { Checkbox, FormControlLabel, withStyles } from '@material-ui/core';
import { CloudDownload } from '@material-ui/icons';
import { useEnqueueStacks } from '@avangard/utils/hooks';
import { Button, Modal } from '@avangard/ui/core';
import { white, black, red } from '@avangard/ui/colors';

import { FileUploader } from '@modules/shared/organisms';
import { UploadSoaDocumentMutation } from '@modules/students/graphql';

import type { ModalProps } from '@avangard/ui/core';
import type { UploadedFile } from '@modules/shared/organisms';
import type { SoaDocumentEntity, SoaDocumentFileEntity } from '@modules/students/entities';
import type {
    UploadSoaDocumentMutationType,
    UploadSoaDocumentMutationVariables,
} from '@modules/types/graphql';

type SoaDocumentModalProps = Omit<ModalProps, 'children'> & {
    document: SoaDocumentEntity;
    documentFile: SoaDocumentFileEntity;
};

const addIcon = <CloudDownload style={{ fontSize: 16 }} />;

const SoaDocumentModal = (props: SoaDocumentModalProps) => {
    const { document, documentFile, open, onClose, ...otherProps } = props;

    const { enqueueError } = useEnqueueStacks();

    const [documentRefChecked, setDocumentRefChecked] = React.useState(
        () => documentFile.documentRef,
    );

    const handleChangeDocumentRef = (_: unknown, checked: boolean) =>
        setDocumentRefChecked(checked);

    const [uploadSoaDocument] = useMutation<
        UploadSoaDocumentMutationType,
        UploadSoaDocumentMutationVariables
    >(UploadSoaDocumentMutation);

    const handleUploadSoaDocument = async (fileId: string | null) => {
        await uploadSoaDocument({
            variables: {
                type: document.type,
                variant: documentFile.variant,
                fileId,
                documentRef: documentRefChecked,
            },
        });
    };

    const handleUploadFile = async (file?: UploadedFile) => {
        try {
            await handleUploadSoaDocument(file?.fileId ?? null);
        } catch (error) {
            enqueueError('При загрузке файла произошла ошибка');
        }
    };

    const handleSaveDocumentFile = async () => {
        try {
            await handleUploadSoaDocument(documentFile?.file.id ?? null);

            onClose();
        } catch (error) {
            enqueueError('При сохранении файла произошла ошибка');
        }
    };

    return (
        <StyledModal title={document.name} open={open} onClose={onClose} {...otherProps}>
            <Content>
                <p>{document.description}</p>
                {document.hint ? <b>{document.hint}</b> : null}
            </Content>

            <Documents>
                <FileUploader
                    fullWidth
                    hideMaxSizeHint
                    maxSize={10}
                    accept='.jpg,.jpeg,.pdf'
                    disabled={!document.canUploadFiles() || documentRefChecked}
                    label={documentFile.uploadTitle}
                    icon={addIcon}
                    file={documentFile.getFileData()}
                    onUploadFile={handleUploadFile}
                />
            </Documents>

            <Footer>
                <FormControlLabel
                    label={documentFile.documentRefTitle}
                    control={
                        <Checkbox
                            disabled={!document.canUploadFiles()}
                            checked={documentRefChecked}
                            onChange={handleChangeDocumentRef}
                        />
                    }
                />

                {document.canUploadFiles() ? (
                    <Button onClick={handleSaveDocumentFile}>Сохранить</Button>
                ) : null}
            </Footer>
        </StyledModal>
    );
};

const StyledModal = withStyles(() => ({
    container: {
        // TODO: поправить в UI кит доступ к header
        '& > div:first-child > div:first-child': {
            position: 'relative',
            fontWeight: 600,

            '&:after': {
                content: '"*"',
                marginLeft: 4,
                color: red[100],
            },
        },
    },
}))(Modal);

const Content = styled.div`
    p {
        color: ${black[80]};
        font-size: 1.4rem;
    }

    b {
        display: inline-flex;

        margin-top: 20px;

        font-size: 1.4rem;
        font-weight: 600;
    }
`;

const Footer = styled.div`
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end;
    width: 100%;

    margin-top: 16px;
`;

const Documents = styled.div`
    width: 100%;
    margin-top: 16px;
    padding: 16px;

    border-radius: 8px;
    background-color: ${white[50]};
`;

export { SoaDocumentModal };
